import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Calendar,
  MapPin,
  Users,
  Plus,
  Trash2,
  Edit,
  Send,
  Euro,
  CheckCircle,
  Clock,
  Beer,
  RefreshCw,
  ChevronRight,
  FileDown,
  Eye,
  AlertCircle,
  X,
  GripVertical,
  Loader2,
  Mail,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function OrganisationEvenementsPage() {
  const { user, token } = useAuth();
  const [events, setEvents] = useState([]);
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [crmClients, setCrmClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showPlanningDialog, setShowPlanningDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    titre: "",
    client: "",
    client_crm_id: "",
    description: "",
    lieu: "",
    adresse: "",
    contact_nom: "",
    contact_telephone: "",
    couverts_estimes: "",
    nombre_food_trucks_requis: 3,
    specialites_recherchees: [],
    dates: [{ date: "", services: [{ type: "midi", horaire_debut: "11:00", horaire_fin: "15:00" }] }],
    has_bar: false,
    bar_details: "",
    frais_par_service: 20,
    bureau_exempt: true,
    rotation_enabled: false,
  });
  
  // Assignment state - now per service slot
  const [servicePlanning, setServicePlanning] = useState({}); // { "2026-05-15_midi": ["ft_id_1", "ft_id_2"], ... }
  const [specialtyFilter, setSpecialtyFilter] = useState("all");
  const [availableSpecialties, setAvailableSpecialties] = useState([]);
  const [searchSpecialty, setSearchSpecialty] = useState("");
  const [interestedFoodTrucks, setInterestedFoodTrucks] = useState([]); // Food trucks interested in this event
  const [showSendSondageDialog, setShowSendSondageDialog] = useState(false);
  const [sendingSondage, setSendingSondage] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [eventsRes, ftRes, crmRes, specialtiesRes] = await Promise.all([
        axios.get(`${API}/api/organised-events`, { headers }),
        axios.get(`${API}/api/food-trucks`, { headers }),
        axios.get(`${API}/api/crm/clients`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/specialites`).catch(() => ({ data: { specialites: [] } })),
      ]);
      
      setEvents(eventsRes.data || []);
      setFoodTrucks(ftRes.data?.filter(ft => ft.statut === "actif") || []);
      setCrmClients(crmRes.data || []);
      setAvailableSpecialties(specialtiesRes.data?.specialites || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create event
  const handleCreateEvent = async () => {
    try {
      // Validation
      if (!formData.titre || !formData.lieu || !formData.dates.some(d => d.date)) {
        toast.error("Veuillez remplir les champs obligatoires");
        return;
      }
      
      const payload = {
        ...formData,
        couverts_estimes: formData.couverts_estimes ? parseInt(formData.couverts_estimes) : null,
        dates: formData.dates.filter(d => d.date),
      };
      
      await axios.post(`${API}/api/organised-events`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Événement créé avec succès");
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error creating event:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  // Update event
  const handleUpdateEvent = async () => {
    if (!selectedEvent) return;
    
    try {
      const payload = {
        ...formData,
        couverts_estimes: formData.couverts_estimes ? parseInt(formData.couverts_estimes) : null,
        dates: formData.dates.filter(d => d.date),
      };
      
      await axios.put(`${API}/api/organised-events/${selectedEvent.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Événement mis à jour");
      setShowCreateDialog(false);
      setSelectedEvent(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Delete event
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer/annuler cet événement ?")) return;
    
    try {
      await axios.delete(`${API}/api/organised-events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Événement supprimé/annulé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Assign food trucks - now based on service planning
  const handleAssignFoodTrucks = async () => {
    if (!selectedEvent) return;
    
    // Build assignments from service planning
    // Each FT gets assigned to the services where they appear
    const ftServices = {};
    Object.entries(servicePlanning).forEach(([slot, ftIds]) => {
      ftIds.forEach(ftId => {
        if (!ftServices[ftId]) ftServices[ftId] = [];
        ftServices[ftId].push(slot);
      });
    });
    
    const assignments = Object.entries(ftServices).map(([ftId, services]) => ({
      food_truck_id: ftId,
      services_assignes: services,
    }));
    
    if (assignments.length === 0) {
      toast.error("Veuillez assigner au moins un food truck à un service");
      return;
    }
    
    try {
      await axios.post(
        `${API}/api/organised-events/${selectedEvent.id}/assign-food-trucks`,
        { assignments, send_notifications: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${assignments.length} food trucks assignés et notifiés`);
      setShowAssignDialog(false);
      setServicePlanning({});
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'assignation");
    }
  };

  // Auto rotation
  const handleAutoRotate = async () => {
    if (!selectedEvent) return;
    
    try {
      await axios.post(
        `${API}/api/organised-events/${selectedEvent.id}/auto-rotate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Rotation automatique appliquée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la rotation");
    }
  };

  // Mark as paid
  const handleMarkPaid = async (eventId, foodTruckId) => {
    try {
      await axios.post(
        `${API}/api/organised-events/${eventId}/mark-paid/${foodTruckId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Paiement enregistré");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement du paiement");
    }
  };

  // Send reminders
  const handleSendReminders = async (eventId) => {
    try {
      const res = await axios.post(
        `${API}/api/organised-events/${eventId}/send-reminders`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.reminders_sent?.length > 0) {
        toast.success(`Relances envoyées à ${res.data.reminders_sent.length} food truck(s)`);
      } else {
        toast.info("Aucune relance à envoyer");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi des relances");
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      titre: "",
      client: "",
      client_crm_id: "",
      description: "",
      lieu: "",
      adresse: "",
      contact_nom: "",
      contact_telephone: "",
      couverts_estimes: "",
      nombre_food_trucks_requis: 3,
      specialites_recherchees: [],
      dates: [{ date: "", services: [{ type: "midi", horaire_debut: "11:00", horaire_fin: "15:00" }] }],
      has_bar: false,
      bar_details: "",
      frais_par_service: 20,
      bureau_exempt: true,
      rotation_enabled: false,
    });
    setSearchSpecialty("");
  };

  // Send sondage to food trucks
  const handleSendSondage = async (event) => {
    try {
      setSendingSondage(true);
      const response = await axios.post(
        `${API}/api/organised-events/${event.id}/send-sondage`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Sondage envoyé à ${response.data.emails_sent} food trucks`);
      fetchData();
      setShowSendSondageDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du sondage");
    } finally {
      setSendingSondage(false);
    }
  };

  // Fetch interested food trucks for an event
  const fetchInterestedFoodTrucks = async (eventId) => {
    try {
      const response = await axios.get(
        `${API}/api/organised-events/${eventId}/interested`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInterestedFoodTrucks(response.data || []);
    } catch (error) {
      console.error("Error fetching interested food trucks:", error);
      setInterestedFoodTrucks([]);
    }
  };

  // Handle specialty selection
  const handleSpecialtyToggle = (specialty, checked) => {
    let newSpecialties = [...formData.specialites_recherchees];
    
    if (specialty === "Toutes") {
      newSpecialties = checked ? ["Toutes"] : [];
    } else {
      newSpecialties = newSpecialties.filter(s => s !== "Toutes");
      if (checked) {
        if (!newSpecialties.includes(specialty)) {
          newSpecialties.push(specialty);
        }
      } else {
        newSpecialties = newSpecialties.filter(s => s !== specialty);
      }
    }
    
    setFormData({ ...formData, specialites_recherchees: newSpecialties });
  };

  // Filter specialties based on search
  const filteredSpecialties = availableSpecialties.filter(spec =>
    spec.toLowerCase().includes(searchSpecialty.toLowerCase())
  );

  // Edit event
  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setFormData({
      ...event,
      couverts_estimes: event.couverts_estimes?.toString() || "",
    });
    setShowCreateDialog(true);
  };

  // Open assign dialog
  const handleOpenAssignDialog = async (event) => {
    setSelectedEvent(event);
    // Fetch interested food trucks for this event
    await fetchInterestedFoodTrucks(event.id);
    // Initialize service planning from existing assignments
    const planning = event.planning || {};
    // Also build from food_trucks if planning is empty
    if (Object.keys(planning).length === 0 && event.food_trucks?.length > 0) {
      event.food_trucks.forEach(ft => {
        (ft.services_assignes || []).forEach(slot => {
          if (!planning[slot]) planning[slot] = [];
          if (!planning[slot].includes(ft.food_truck_id)) {
            planning[slot].push(ft.food_truck_id);
          }
        });
      });
    }
    setServicePlanning(planning);
    setShowAssignDialog(true);
  };

  // Add FT to a service slot
  const addFtToSlot = (slot, ftId) => {
    const current = servicePlanning[slot] || [];
    if (!current.includes(ftId)) {
      setServicePlanning({
        ...servicePlanning,
        [slot]: [...current, ftId]
      });
    }
  };

  // Remove FT from a service slot
  const removeFtFromSlot = (slot, ftId) => {
    const current = servicePlanning[slot] || [];
    setServicePlanning({
      ...servicePlanning,
      [slot]: current.filter(id => id !== ftId)
    });
  };

  // Auto-fill planning with round-robin by specialty
  const handleAutoFillPlanning = () => {
    if (!selectedEvent?.all_service_slots) return;
    
    // Group FT by specialty
    const ftBySpecialty = {};
    foodTrucks.forEach(ft => {
      const spec = ft.specialite || ft.specialites?.[0] || "Autre";
      if (!ftBySpecialty[spec]) ftBySpecialty[spec] = [];
      ftBySpecialty[spec].push(ft.id);
    });
    
    // Round-robin fill
    const newPlanning = {};
    const indices = {};
    Object.keys(ftBySpecialty).forEach(spec => indices[spec] = 0);
    
    selectedEvent.all_service_slots.forEach(slot => {
      newPlanning[slot] = [];
      Object.entries(ftBySpecialty).forEach(([spec, ftIds]) => {
        if (ftIds.length > 0) {
          const idx = indices[spec] % ftIds.length;
          newPlanning[slot].push(ftIds[idx]);
          indices[spec]++;
        }
      });
    });
    
    setServicePlanning(newPlanning);
    toast.success("Planning rempli automatiquement par rotation");
  };

  // Add date to form
  const addDate = () => {
    setFormData({
      ...formData,
      dates: [...formData.dates, { date: "", services: [{ type: "midi", horaire_debut: "11:00", horaire_fin: "15:00" }] }],
    });
  };

  // Remove date from form
  const removeDate = (index) => {
    setFormData({
      ...formData,
      dates: formData.dates.filter((_, i) => i !== index),
    });
  };

  // Add service to date
  const addService = (dateIndex) => {
    const newDates = [...formData.dates];
    newDates[dateIndex].services.push({ type: "soir", horaire_debut: "18:00", horaire_fin: "23:00" });
    setFormData({ ...formData, dates: newDates });
  };

  // Remove service from date
  const removeService = (dateIndex, serviceIndex) => {
    const newDates = [...formData.dates];
    newDates[dateIndex].services = newDates[dateIndex].services.filter((_, i) => i !== serviceIndex);
    setFormData({ ...formData, dates: newDates });
  };

  // Get unique specialties
  const uniqueSpecialties = [...new Set(foodTrucks.map(ft => ft.specialite || (ft.specialites?.[0]) || "Autre").filter(Boolean))];

  // Filter food trucks by specialty
  const filteredFoodTrucks = specialtyFilter === "all" 
    ? foodTrucks 
    : foodTrucks.filter(ft => (ft.specialite || ft.specialites?.[0]) === specialtyFilter);

  // Status badge
  const getStatusBadge = (statut) => {
    const config = {
      brouillon: { label: "Brouillon", variant: "secondary" },
      en_cours: { label: "En cours", variant: "warning" },
      confirme: { label: "Confirmé", variant: "success" },
      termine: { label: "Terminé", variant: "default" },
      annule: { label: "Annulé", variant: "destructive" },
    };
    const { label, variant } = config[statut] || { label: statut, variant: "default" };
    return <Badge variant={variant}>{label}</Badge>;
  };

  // Payment status badge
  const getPaymentBadge = (statut) => {
    if (statut === "paye") return <Badge className="bg-green-500">Payé</Badge>;
    if (statut === "exempte") return <Badge className="bg-blue-500">Exempté</Badge>;
    return <Badge variant="warning">En attente</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="organisation-evenements-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Organisation Événements</h1>
          <p className="text-gray-500 text-sm mt-1">Créez et gérez vos événements avec les food trucks</p>
        </div>
        <Button className="w-full sm:w-auto" onClick={() => { resetForm(); setSelectedEvent(null); setShowCreateDialog(true); }} data-testid="create-event-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nouvel Événement
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-2xl font-bold">{events.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">En cours</p>
                <p className="text-2xl font-bold">{events.filter(e => e.statut === "en_cours").length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Confirmés</p>
                <p className="text-2xl font-bold">{events.filter(e => e.statut === "confirme").length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Food Trucks</p>
                <p className="text-2xl font-bold">{foodTrucks.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Events List */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Événements</CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucun événement créé</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Créer un événement
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {events.map((event) => (
                <div 
                  key={event.id} 
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  data-testid={`event-card-${event.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{event.titre}</h3>
                        {getStatusBadge(event.statut)}
                        {event.has_bar && (
                          <Badge variant="outline" className="gap-1">
                            <Beer className="h-3 w-3" /> Bar
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {event.lieu}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {event.dates?.length > 0 && format(parseISO(event.dates[0].date), "d MMM yyyy", { locale: fr })}
                          {event.dates?.length > 1 && ` - ${format(parseISO(event.dates[event.dates.length - 1].date), "d MMM yyyy", { locale: fr })}`}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {event.food_trucks?.length || 0} food trucks
                        </span>
                        {event.client && (
                          <span className="text-gray-500">Client: {event.client}</span>
                        )}
                      </div>

                      {/* Payment Progress */}
                      {event.food_trucks?.length > 0 && (
                        <div className="mt-3">
                          <div className="flex items-center gap-2 text-sm">
                            <Euro className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">
                              Paiements: {event.payment_progress?.paid || 0}/{event.payment_progress?.total || 0}
                            </span>
                            <div className="flex-1 h-2 bg-gray-200 rounded-full max-w-[200px]">
                              <div 
                                className="h-full bg-green-500 rounded-full transition-all"
                                style={{ 
                                  width: `${event.payment_progress?.total > 0 
                                    ? (event.payment_progress.paid / event.payment_progress.total) * 100 
                                    : 0}%` 
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      <Button variant="outline" size="sm" onClick={() => { setSelectedEvent(event); setShowDetailDialog(true); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleEditEvent(event)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      {/* Send sondage button - only if no food trucks assigned yet */}
                      {(!event.food_trucks || event.food_trucks.length === 0) && !event.sondage_sent && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-blue-600"
                          onClick={() => { setSelectedEvent(event); setShowSendSondageDialog(true); }}
                          title="Envoyer le sondage aux food trucks"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      )}
                      {/* Show interested count if sondage sent */}
                      {event.sondage_sent && (
                        <Badge variant="outline" className="text-green-600">
                          {event.interested_count || 0} intéressés
                        </Badge>
                      )}
                      <Button variant="outline" size="sm" onClick={() => handleOpenAssignDialog(event)}>
                        <Users className="h-4 w-4" />
                      </Button>
                      {event.food_trucks?.length > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => window.open(`${API}/api/organised-events/${event.id}/export-pdf`, '_blank')}
                          title="Exporter en PDF"
                        >
                          <FileDown className="h-4 w-4" />
                        </Button>
                      )}
                      {event.food_trucks?.length > 0 && event.food_trucks.some(ft => ft.statut_paiement === "en_attente") && (
                        <Button variant="outline" size="sm" onClick={() => handleSendReminders(event.id)}>
                          <Mail className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteEvent(event.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Event Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{selectedEvent ? "Modifier l'événement" : "Créer un événement"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4 overflow-y-auto flex-1 pr-2">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Titre de l'événement *</Label>
                <Input
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  placeholder="Festival Gastronomique 2026"
                  data-testid="event-title-input"
                />
              </div>
              
              <div>
                <Label>Client / Organisateur</Label>
                <Input
                  value={formData.client}
                  onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                  placeholder="Mairie de Paris"
                />
              </div>
              
              <div>
                <Label>Client CRM (optionnel)</Label>
                <Select 
                  value={formData.client_crm_id || "none"} 
                  onValueChange={(v) => setFormData({ ...formData, client_crm_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Lier à un client CRM" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {crmClients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.nom} - {client.entreprise}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Lieu *</Label>
                <Input
                  value={formData.lieu}
                  onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
                  placeholder="Parc des Expositions"
                  data-testid="event-lieu-input"
                />
              </div>
              
              <div>
                <Label>Adresse complète</Label>
                <Input
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                  placeholder="1 Place de la Porte de Versailles, 75015 Paris"
                />
              </div>

              <div>
                <Label>Contact sur place</Label>
                <Input
                  value={formData.contact_nom}
                  onChange={(e) => setFormData({ ...formData, contact_nom: e.target.value })}
                  placeholder="Jean Dupont"
                />
              </div>
              
              <div>
                <Label>Téléphone contact</Label>
                <Input
                  value={formData.contact_telephone}
                  onChange={(e) => setFormData({ ...formData, contact_telephone: e.target.value })}
                  placeholder="06 XX XX XX XX"
                />
              </div>

              <div>
                <Label>Couverts estimés</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formData.couverts_estimes}
                  onChange={(e) => setFormData({ ...formData, couverts_estimes: e.target.value })}
                  placeholder="500"
                />
              </div>

              <div>
                <Label>Nombre de food trucks souhaités *</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formData.nombre_food_trucks_requis}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({ ...formData, nombre_food_trucks_requis: val === "" ? 1 : parseInt(val) || 1 });
                  }}
                  data-testid="nombre-ft-input"
                  placeholder="1"
                />
              </div>

              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description de l'événement..."
                  rows={3}
                />
              </div>
            </div>

            {/* Spécialités recherchées */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Spécialités recherchées</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Sélectionnez les spécialités pour filtrer les food trucks qui recevront le sondage
              </p>
              
              <div className="relative mb-2">
                <Input
                  type="text"
                  placeholder="Rechercher une spécialité..."
                  value={searchSpecialty}
                  onChange={(e) => setSearchSpecialty(e.target.value)}
                  className="pl-3"
                />
              </div>
              
              {formData.specialites_recherchees.length > 0 && !formData.specialites_recherchees.includes("Toutes") && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {formData.specialites_recherchees.map(spec => (
                    <Badge key={spec} variant="secondary" className="bg-primary text-white">
                      {spec}
                      <button
                        type="button"
                        onClick={() => handleSpecialtyToggle(spec, false)}
                        className="ml-1 hover:text-red-200"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 border rounded-md max-h-48 overflow-y-auto bg-gray-50">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="spec-toutes"
                    checked={formData.specialites_recherchees.includes("Toutes")}
                    onCheckedChange={(checked) => handleSpecialtyToggle("Toutes", checked)}
                  />
                  <label htmlFor="spec-toutes" className="text-sm cursor-pointer font-semibold text-primary">
                    Toutes les spécialités
                  </label>
                </div>
                {(searchSpecialty ? filteredSpecialties : availableSpecialties.slice(0, 50)).map((spec) => {
                  const isCategory = ["Salé", "Sucré", "Boissons"].includes(spec);
                  return (
                    <div key={spec} className={`flex items-center space-x-2 ${isCategory ? "col-span-2 md:col-span-3 bg-gray-200 p-2 rounded-md mt-2 -mx-1" : ""}`}>
                      <Checkbox
                        id={`spec-${spec}`}
                        checked={formData.specialites_recherchees.includes(spec)}
                        onCheckedChange={(checked) => handleSpecialtyToggle(spec, checked)}
                        disabled={formData.specialites_recherchees.includes("Toutes")}
                      />
                      <label htmlFor={`spec-${spec}`} className={`text-sm cursor-pointer truncate ${isCategory ? "font-bold text-secondary text-base" : ""}`}>
                        {isCategory ? `🏷️ ${spec}` : spec}
                      </label>
                    </div>
                  );
                })}
              </div>
              {formData.specialites_recherchees.includes("Toutes") && (
                <p className="text-xs text-primary mt-1 font-medium">
                  Tous les food trucks recevront le sondage
                </p>
              )}
            </div>

            {/* Dates and Services */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-base font-semibold">Dates et Services</Label>
                <Button type="button" variant="outline" size="sm" onClick={addDate}>
                  <Plus className="h-4 w-4 mr-1" /> Ajouter une date
                </Button>
              </div>
              
              {formData.dates.map((dateInfo, dateIndex) => (
                <div key={dateIndex} className="border rounded-lg p-4 mb-4 bg-gray-50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={dateInfo.date}
                        onChange={(e) => {
                          const newDates = [...formData.dates];
                          newDates[dateIndex].date = e.target.value;
                          setFormData({ ...formData, dates: newDates });
                        }}
                        className="w-48"
                      />
                    </div>
                    {formData.dates.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeDate(dateIndex)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm">Services</Label>
                    {dateInfo.services.map((service, serviceIndex) => (
                      <div key={serviceIndex} className="flex items-center gap-3 bg-white p-2 rounded">
                        <Select
                          value={service.type}
                          onValueChange={(v) => {
                            const newDates = [...formData.dates];
                            newDates[dateIndex].services[serviceIndex].type = v;
                            setFormData({ ...formData, dates: newDates });
                          }}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="midi">Midi</SelectItem>
                            <SelectItem value="soir">Soir</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <Input
                          type="time"
                          value={service.horaire_debut}
                          onChange={(e) => {
                            const newDates = [...formData.dates];
                            newDates[dateIndex].services[serviceIndex].horaire_debut = e.target.value;
                            setFormData({ ...formData, dates: newDates });
                          }}
                          className="w-28"
                        />
                        <span>-</span>
                        <Input
                          type="time"
                          value={service.horaire_fin}
                          onChange={(e) => {
                            const newDates = [...formData.dates];
                            newDates[dateIndex].services[serviceIndex].horaire_fin = e.target.value;
                            setFormData({ ...formData, dates: newDates });
                          }}
                          className="w-28"
                        />
                        
                        {dateInfo.services.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeService(dateIndex, serviceIndex)}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button type="button" variant="ghost" size="sm" onClick={() => addService(dateIndex)}>
                      <Plus className="h-4 w-4 mr-1" /> Ajouter un service
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Options */}
            <div className="border-t pt-4 space-y-4">
              <Label className="text-base font-semibold">Options</Label>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Bar présent</Label>
                  <p className="text-sm text-gray-500">Un bar sera présent sur l'événement</p>
                </div>
                <Switch
                  checked={formData.has_bar}
                  onCheckedChange={(v) => setFormData({ ...formData, has_bar: v })}
                />
              </div>
              
              {formData.has_bar && (
                <div>
                  <Label>Détails du bar</Label>
                  <Input
                    value={formData.bar_details}
                    onChange={(e) => setFormData({ ...formData, bar_details: e.target.value })}
                    placeholder="2 Beer Trucks, 1 bar cocktails..."
                  />
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <Label>Rotation automatique</Label>
                  <p className="text-sm text-gray-500">Activer la rotation des food trucks entre les services</p>
                </div>
                <Switch
                  checked={formData.rotation_enabled}
                  onCheckedChange={(v) => setFormData({ ...formData, rotation_enabled: v })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Frais par service (€)</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={formData.frais_par_service === 0 ? "" : formData.frais_par_service}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({ ...formData, frais_par_service: val === "" ? 0 : parseFloat(val) || 0 });
                    }}
                    placeholder="0"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Checkbox
                    id="bureau_exempt"
                    checked={formData.bureau_exempt}
                    onCheckedChange={(v) => setFormData({ ...formData, bureau_exempt: v })}
                  />
                  <Label htmlFor="bureau_exempt">Bureau exempté des frais</Label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annuler</Button>
            <Button onClick={selectedEvent ? handleUpdateEvent : handleCreateEvent} data-testid="save-event-btn">
              {selectedEvent ? "Mettre à jour" : "Créer l'événement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Food Trucks Dialog - New Service-based Planning */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Planning Food Trucks - {selectedEvent?.titre}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Interested Food Trucks Section */}
            {interestedFoodTrucks.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Food Trucks intéressés ({interestedFoodTrucks.length})
                </h4>
                <p className="text-sm text-green-700 mb-3">
                  Ces food trucks ont répondu "Intéressé" au sondage
                </p>
                <div className="flex flex-wrap gap-2">
                  {interestedFoodTrucks.map(ft => (
                    <Badge key={ft.id} className="bg-green-600">
                      {ft.nom_food_truck} - {ft.specialite || ft.specialites?.[0] || "N/A"}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Label>Filtrer FT:</Label>
                <Select value={specialtyFilter} onValueChange={setSpecialtyFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes spécialités</SelectItem>
                    <SelectItem value="interested">Intéressés uniquement</SelectItem>
                    {uniqueSpecialties.map((spec) => (
                      <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button variant="outline" size="sm" onClick={handleAutoFillPlanning}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Rotation auto
              </Button>
            </div>

            {/* Planning Table - One row per service */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium w-40">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium w-24">Service</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Food Trucks assignés</th>
                    <th className="px-4 py-3 text-left text-sm font-medium w-64">Ajouter</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {selectedEvent?.all_service_slots?.map((slot) => {
                    const [dateStr, serviceType] = slot.split("_");
                    const assignedFts = servicePlanning[slot] || [];
                    
                    // Filter food trucks - prioritize interested ones
                    const availableFts = specialtyFilter === "interested"
                      ? interestedFoodTrucks.filter(ft => !assignedFts.includes(ft.id))
                      : filteredFoodTrucks.filter(ft => !assignedFts.includes(ft.id));
                    
                    return (
                      <tr key={slot} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium">
                          {format(parseISO(dateStr), "EEE d MMM", { locale: fr })}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={serviceType === "midi" ? "default" : "secondary"}>
                            {serviceType === "midi" ? "Midi" : "Soir"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            {assignedFts.length === 0 ? (
                              <span className="text-gray-400 text-sm italic">Aucun FT assigné</span>
                            ) : (
                              assignedFts.map(ftId => {
                                const ft = foodTrucks.find(f => f.id === ftId);
                                if (!ft) return null;
                                return (
                                  <Badge 
                                    key={ftId} 
                                    variant="outline" 
                                    className="flex items-center gap-1 pr-1"
                                  >
                                    <span className="text-xs">
                                      {ft.nom_food_truck}
                                    </span>
                                    <button
                                      onClick={() => removeFtFromSlot(slot, ftId)}
                                      className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </Badge>
                                );
                              })
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Select
                            value=""
                            onValueChange={(ftId) => {
                              if (ftId) addFtToSlot(slot, ftId);
                            }}
                          >
                            <SelectTrigger className="w-full h-8 text-sm">
                              <SelectValue placeholder="+ Ajouter un FT" />
                            </SelectTrigger>
                            <SelectContent>
                              {/* Show interested FTs first with a green indicator */}
                              {availableFts.length === 0 ? (
                                <SelectItem value="none" disabled>Aucun food truck disponible</SelectItem>
                              ) : (
                                availableFts.map(ft => {
                                  const isInterested = interestedFoodTrucks.some(ift => ift.id === ft.id);
                                  return (
                                    <SelectItem key={ft.id} value={ft.id}>
                                      <span className="flex items-center gap-2">
                                        {isInterested && <CheckCircle className="h-3 w-3 text-green-600" />}
                                        {ft.nom_food_truck}
                                        <span className="text-gray-400 text-xs">
                                          ({ft.specialite || ft.specialites?.[0] || "N/A"})
                                        </span>
                                      </span>
                                    </SelectItem>
                                  );
                                })
                              )}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            <div className="bg-gray-100 rounded-lg p-4">
              <h4 className="font-medium mb-3">Récapitulatif par Food Truck</h4>
              <div className="space-y-2">
                {(() => {
                  // Calculate FT summary
                  const ftSummary = {};
                  Object.entries(servicePlanning).forEach(([slot, ftIds]) => {
                    ftIds.forEach(ftId => {
                      if (!ftSummary[ftId]) ftSummary[ftId] = { count: 0, slots: [] };
                      ftSummary[ftId].count++;
                      ftSummary[ftId].slots.push(slot);
                    });
                  });
                  
                  const summaryEntries = Object.entries(ftSummary);
                  if (summaryEntries.length === 0) {
                    return <p className="text-sm text-gray-500">Aucun food truck assigné</p>;
                  }
                  
                  return (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {summaryEntries.map(([ftId, data]) => {
                        const ft = foodTrucks.find(f => f.id === ftId);
                        if (!ft) return null;
                        const amount = data.count * (selectedEvent?.frais_par_service || 20);
                        return (
                          <div key={ftId} className="flex justify-between items-center bg-white p-2 rounded">
                            <span className="font-medium">{ft.nom_food_truck}</span>
                            <span className="text-gray-500">
                              {data.count} service{data.count > 1 ? "s" : ""} • {amount}€
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Annuler</Button>
            <Button onClick={handleAssignFoodTrucks} data-testid="assign-ft-btn">
              <Send className="h-4 w-4 mr-2" />
              Assigner et Notifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent?.titre}
              {selectedEvent && getStatusBadge(selectedEvent.statut)}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-6 py-4">
              {/* Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Lieu:</span>
                  <span className="ml-2 font-medium">{selectedEvent.lieu}</span>
                </div>
                <div>
                  <span className="text-gray-500">Client:</span>
                  <span className="ml-2 font-medium">{selectedEvent.client || "-"}</span>
                </div>
                <div>
                  <span className="text-gray-500">Contact:</span>
                  <span className="ml-2 font-medium">{selectedEvent.contact_nom} {selectedEvent.contact_telephone}</span>
                </div>
                <div>
                  <span className="text-gray-500">Couverts:</span>
                  <span className="ml-2 font-medium">{selectedEvent.couverts_estimes || "-"}</span>
                </div>
              </div>

              {/* Dates */}
              <div>
                <h4 className="font-medium mb-2">Dates et Services</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedEvent.all_service_slots?.map((slot) => {
                    const [date, type] = slot.split("_");
                    return (
                      <Badge key={slot} variant="outline">
                        {format(parseISO(date), "d MMM", { locale: fr })} - {type}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              {/* Food Trucks */}
              <div>
                <h4 className="font-medium mb-2">Food Trucks Assignés ({selectedEvent.food_trucks?.length || 0})</h4>
                {selectedEvent.food_trucks?.length > 0 ? (
                  <div className="border rounded-lg divide-y">
                    {selectedEvent.food_trucks.map((ft) => (
                      <div key={ft.food_truck_id} className="p-3 flex items-center justify-between">
                        <div>
                          <div className="font-medium">{ft.food_truck_name}</div>
                          <div className="text-sm text-gray-500">
                            {ft.nombre_services} service(s) • {ft.montant_du}€
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getPaymentBadge(ft.statut_paiement)}
                          {ft.statut_paiement === "en_attente" && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleMarkPaid(selectedEvent.id, ft.food_truck_id)}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Aucun food truck assigné</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Fermer</Button>
            <Button onClick={() => { setShowDetailDialog(false); handleOpenAssignDialog(selectedEvent); }}>
              <Users className="h-4 w-4 mr-2" />
              Gérer les Food Trucks
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Sondage Confirmation Dialog */}
      <Dialog open={showSendSondageDialog} onOpenChange={setShowSendSondageDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Envoyer le sondage</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-sm text-gray-600">
              Vous êtes sur le point d'envoyer un sondage aux food trucks pour l'événement :
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold">{selectedEvent?.titre}</h4>
              <p className="text-sm text-gray-500">{selectedEvent?.lieu}</p>
              {selectedEvent?.dates?.length > 0 && (
                <p className="text-sm text-gray-500">
                  {selectedEvent.dates.length} date(s) - {selectedEvent.dates.map(d => d.date).join(", ")}
                </p>
              )}
              {selectedEvent?.nombre_food_trucks_requis && (
                <p className="text-sm text-primary font-medium mt-2">
                  {selectedEvent.nombre_food_trucks_requis} food trucks recherchés
                </p>
              )}
            </div>
            
            <div className="text-sm">
              <p className="font-medium mb-1">Spécialités ciblées :</p>
              {selectedEvent?.specialites_recherchees?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {selectedEvent.specialites_recherchees.map(spec => (
                    <Badge key={spec} variant="secondary">{spec}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Toutes les spécialités</p>
              )}
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                Les food trucks correspondant aux spécialités recevront un email pour indiquer leur intérêt.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendSondageDialog(false)}>Annuler</Button>
            <Button 
              onClick={() => handleSendSondage(selectedEvent)} 
              disabled={sendingSondage}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingSondage ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Envoyer le sondage
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
